import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackCompleteRegistration } from '../utils/metaPixel';
import styled from 'styled-components';

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  padding: 0 20px;
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #4285F4;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: #333;
`;

const ErrorMessage = styled.div`
  color: #d93025;
  margin-top: 16px;
  max-width: 400px;
`;

const GoogleCallback: React.FC = () => {
  const navigate = useNavigate();
  const { googleLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  
  // Use a ref to track if the auth process has already been initiated
  const authProcessed = useRef(false);

  useEffect(() => {    
    const handleCallback = async () => {
      // Prevent processing the code multiple times
      if (authProcessed.current) {
        return;
      }
      
      try {
        // Mark as processed immediately to prevent race conditions
        authProcessed.current = true;
        
        // Get the authorization code from URL
        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        
        // Handle error from Google OAuth
        if (error) {
          setError(
            error === 'access_denied' 
              ? 'Sign in was canceled. Please try again.'
              : `Error during Google sign in: ${error}`
          );
          setStatus('error');
          return;
        }

        // Validate code existence
        if (!code) {
          setError('No authorization code received from Google');
          setStatus('error');
          return;
        }
        
        // Process login
        const { success, isNewUser } = await googleLogin(code);

        if (success) {
          setStatus('success');

          // Only a brand-new account is a registration — this callback also
          // runs for every returning user signing back in.
          if (isNewUser) {
            trackCompleteRegistration('google');
          }

          // Check if we should redirect to a specific page
          const redirectPath = sessionStorage.getItem('auth_redirect');
          if (redirectPath) {
            sessionStorage.removeItem('auth_redirect');
            navigate(redirectPath);
          } else {
            navigate('/');
          }
        } else {
          setError('Failed to log in with Google. Please try again.');
          setStatus('error');
        }
      } catch (err) {
        console.error('[GoogleCallback] Error:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        setStatus('error');
      }
    };

    handleCallback();
    
    // Cleanup function to clear URL parameters on unmount
    return () => {
      // Replace current URL without query parameters to prevent reuse
      if (window.history && window.history.replaceState) {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };
  }, [googleLogin, navigate]);

  // Show appropriate UI based on status
  return (
    <CallbackContainer>
      {status === 'loading' && (
        <>
          <Spinner />
          <Message>Signing in with Google...</Message>
        </>
      )}
      
      {status === 'error' && (
        <>
          <Message>Sign In Failed</Message>
          <ErrorMessage>{error}</ErrorMessage>
          <button 
            onClick={() => navigate('/login')}
            style={{ 
              marginTop: '20px',
              padding: '10px 20px',
              background: '#4285F4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Return to Login
          </button>
        </>
      )}
    </CallbackContainer>
  );
};

export default GoogleCallback;